import { Dispatch } from 'react-redux';
import { AuthActionCreators, IApplicationState, IRecipientLookupResponse } from "../";
import { IPost, IGet } from '../authorization';
import {
    IAntibodyScreening,
    IAntibodyScreeningResult,
    IBaseResponse, IDonorDeclines, IFileUpload, IPostTransplantRejection, IRecipient, IRecipientActivityLog, IRecipientContactInformation,
    IRecipientFinancialInfo, IRecipientFinancialInfoPost, IRecipientGeneral, IRecipientGeneralDataRequest, IRecipientMedicalDetails,
    IRecipientMedicalDetailsRequest, IRecipientProfileHeaderInfo, IRecipientProfileHlaInfo,
    IRecipientProfileHlaInfoPost, IRecipientProfilePostTransplantData, IRecipientProfilePostTransplantDataRequest, IRecipientProfileRecords, IRecipientVoucher, IRecipientXm
} from '@nkr/models/index';

export interface IRecipientHeaderInfoResponse {
    data: {
        success: boolean,
        message: string,
        results: IRecipientProfileHeaderInfo
    }
}

export interface IRecipientGeneralDataResponse {
    data: {
        success: boolean,
        message: string,
        results: IRecipientGeneral
    }
}

export interface IRecipientContactInfoResponse {
    data: {
        success: boolean,
        message: string,
        results: IRecipientContactInformation
    }
}

export interface IRecipientMedicalDetailsResponse {
    data: {
        success: boolean,
        message: string,
        results: IRecipientMedicalDetails
    }
}

export interface IRecipientProfileHlaInfoResponse {
    data: {
        success: boolean,
        results: IRecipientProfileHlaInfo
    }
}

export interface IRecipientVoucherResponse {
    data: {
        success: boolean,
        message: string,
        results: IRecipientVoucher
    }
}

export interface IRecipientProfileRecordsResponse {
    data: {
        success: boolean,
        results: IRecipientProfileRecords
    }
}

export interface IRecipientProfileRecordsPostResponse {
    data: {
        success: boolean
    }
}

export interface IRecipientFinancialInfoResponse {
    data: {
        success: boolean,
        results: IRecipientFinancialInfo
    }
}

export interface IRecipientFinancialInfoPostResponse {
    data: {
        success: boolean
    }
}

export interface IRecipientActivityLogResponse {
    data: {
        success: boolean,
        message: string,
        results: IRecipientActivityLog[]
    }
}

export interface IRecipientDonorDeclinesResponse {
    data: {
        success: boolean,
        recipientData: {
            recipient: IRecipient,
            declineRatioAllTime: number,
            potentialDonors: IDonorDeclines[]
        }
    }
}

export interface IRecipientXmResponse {
    data: {
        success: boolean,
        message: string,
        alias: string,
        results: IRecipientXm[]
    }
}

export interface IRecipientPostTransplantResponse {
    data: {
        success: boolean,
        message: string,
        results: IRecipientProfilePostTransplantData
    }
}

export interface IAntibodyScreeningResponse {
    data: {
        success: boolean,
        message: string,
        results: IAntibodyScreening[]
    }
}

export interface IAntibodyScreeningResultResponse {
    data: {
        success: boolean,
        message: string,
        results: IAntibodyScreeningResult[]
    }
}

export interface IPostTransplantRejectionEpisodeResponse {
    data: {
        success: boolean,
        results: IPostTransplantRejection[]
    }
}

export interface IPairedDonor {
    id: number,
    centerId: number,
    alias: string,
    age: number,
    bloodType: string,
    gender: string,
    isExportable: boolean,
    isWillingToTravel: boolean,
    isBridgeDonor: boolean,
    registrationDate: string
}

export interface IRecipientPairedDonorsListResponse {
    data: {
        success: boolean,
        results: IPairedDonor[]
    }
}

export const ActionCreators = {
    getRecipientProfileLookupData: (centerId: number, recipientId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IRecipientLookupResponse> => {
        let options: IGet = {
            url: `${__API__}/center/${centerId}/recipient/${recipientId}/lookup-data`
        };

        return await dispatch(AuthActionCreators.apiGet(options));
    },

    getHeaderinfo: (centerId: number, recipientId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IRecipientHeaderInfoResponse> => {
        let options: IGet = {
            url: `${__API__}/center/${centerId}/recipient/${recipientId}/header-info`
        };

        return await dispatch(AuthActionCreators.apiGet(options));
    },

    getGeneral: (centerId: number, recipientId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IRecipientGeneralDataResponse> => {
        let options: IGet = {
            url: `${__API__}/center/${centerId}/recipient/${recipientId}/general-info`
        };

        return await dispatch(AuthActionCreators.apiGet(options));
    },
    updateGeneral: (centerId: number, recipientId: number, data: IRecipientGeneralDataRequest) => async (dispatch: Dispatch<IApplicationState>): Promise<IRecipientGeneralDataResponse> => {
        let options = {
            url: `${__API__}/center/${centerId}/recipient/${recipientId}/general-info`,
            data
        };

        return await dispatch(AuthActionCreators.apiPut(options));
    },

    getContactInfo: (centerId: number, recipientId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IRecipientContactInfoResponse> => {
        let options: IGet = {
            url: `${__API__}/center/${centerId}/recipient/${recipientId}/contact-info`
        };

        return await dispatch(AuthActionCreators.apiGet(options));
    },
    updateContactInfo: (centerId: number, recipientId: number, data: IRecipientContactInformation) => async (dispatch: Dispatch<IApplicationState>): Promise<IRecipientContactInfoResponse> => {
        let options = {
            url: `${__API__}/center/${centerId}/recipient/${recipientId}/contact-info`,
            data
        };

        return await dispatch(AuthActionCreators.apiPut(options));
    },

    getRecipientMedicalDetails: (centerId: number, recipientId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IRecipientMedicalDetailsResponse> => {
        let options = {
            url: `${__API__}/center/${centerId}/recipient/${recipientId}/medical-details`
        }

        return await dispatch(AuthActionCreators.apiGet(options))
    },
    postRecipientMedicalDetails: (centerId: number, recipientId: number, data: IRecipientMedicalDetailsRequest) => async (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options = {
            url: `${__API__}/center/${centerId}/recipient/${recipientId}/medical-details`,
            data
        }

        return await dispatch(AuthActionCreators.apiPost(options))
    },

    getHlaInfoData: (centerId: number, recipientId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IRecipientProfileHlaInfoResponse> => {
        let options: IGet = {
            url: `${__API__}/center/${centerId}/recipient/${recipientId}/hla-info`
        };

        return await dispatch(AuthActionCreators.apiGet(options));
    },
    postHlaInfo: (centerId: number, recipientId: number, data: IRecipientProfileHlaInfoPost) => async (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options: IPost = {
            url: `${__API__}/center/${centerId}/recipient/${recipientId}/hla-info`,
            data
        };

        return await dispatch(AuthActionCreators.apiPost(options));
    },
    postAutoPopulateHLA: (recipientId: number, centerId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options: IPost = {
            url: `${__API__}/center/${centerId}/recipient/${recipientId}/auto-populate-hla`,
        };

        return await dispatch(AuthActionCreators.apiPost(options));
    },
    postConfirmHLADiscrepancy: (recipientId: number, centerId: number, initials: string) => async (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options = {
            url: `${__API__}/center/${centerId}/recipient/${recipientId}/confirm-hla-discrepancy`,
            data: {
                recipientId,
                initials
            }
        };

        return await dispatch(AuthActionCreators.apiPost(options));
    },
    getVoucher: (centerId: number, recipientId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IRecipientVoucherResponse> => {
        let options: IGet = {
            url: `${__API__}/recipient/${recipientId}/recipient-voucher?centerId=${centerId}`
        };

        return await dispatch(AuthActionCreators.apiGet(options));
    },
    updateVoucher: (centerId: number, recipientId: number, recipientVoucher: IRecipientVoucher) => async (dispatch: Dispatch<IApplicationState>): Promise<IRecipientVoucherResponse> => {
        let {
            adpFirstName,
            adpLastName,
            adpAddress,
            adpCity,
            adpState,
            adpPostalCode,
            adpPhonePrimary,
            adpPhoneSecondary,
            adpEmailPrimary,
            adpEmailSecondary } = recipientVoucher;

        let options = {
            url: `${__API__}/recipient/${centerId}/recipient-voucher?id=${recipientId}`,
            data: {
                centerId,
                adpFirstName,
                adpLastName,
                adpAddress,
                adpCity,
                adpState,
                adpPostalCode,
                adpPhonePrimary,
                adpPhoneSecondary,
                adpEmailPrimary,
                adpEmailSecondary
            }
        };
        return await dispatch(AuthActionCreators.apiPut(options));
    },

    getRecipientRecords: (centerId: number, recipientId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IRecipientProfileRecordsResponse> => {
        let options: IGet = {
            url: `${__API__}/center/${centerId}/recipient/${recipientId}/records`
        };

        return await dispatch(AuthActionCreators.apiGet(options));
    },
    postRecipientRecords: (centerId: number, recipientId: number, docType: number, file: IFileUpload) => async (dispatch: Dispatch<IApplicationState>): Promise<IRecipientProfileRecordsPostResponse> => {
        var formData = new FormData;
        formData.append('docType', docType.toString());
        formData.append('file', file.file[0]);

        let options: IPost = {
            url: `${__API__}/center/${centerId}/recipient/${recipientId}/records`,
            data: formData
        };

        return await dispatch(AuthActionCreators.apiPostFormData(options));
    },

    getFinancialInfo: (centerId: number, recipientId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IRecipientFinancialInfoResponse> => {
        let options = {
            url: `${__API__}/center/${centerId}/recipient/${recipientId}/financial-info`
        }

        return await dispatch(AuthActionCreators.apiGet(options))
    },
    updateFinancialInfo: (centerId: number, recipientId: number, data: IRecipientFinancialInfoPost) => async (dispatch: Dispatch<IApplicationState>): Promise<IRecipientFinancialInfoPostResponse> => {
        let options = {
            url: `${__API__}/center/${centerId}/recipient/${recipientId}/financial-info`,
            data
        }

        return await dispatch(AuthActionCreators.apiPost(options))
    },

    getActivityLog: (centerId: number, recipientId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IRecipientActivityLogResponse> => {
        let options: IGet = {
            url: `${__API__}/center/${centerId}/recipient/${recipientId}/activity-log`
        };

        return await dispatch(AuthActionCreators.apiGet(options));
    },

    getDonorDeclines: (centerId: number, recipientId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IRecipientDonorDeclinesResponse> => {
        let options: IGet = {
            url: `${__API__}/center/${centerId}/recipient/${recipientId}/donor-declines`
        };

        return await dispatch(AuthActionCreators.apiGet(options));
    },

    getXmRecords: (centerId: number, recipientId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IRecipientXmResponse> => {
        let options: IGet = {
            url: `${__API__}/center/${centerId}/recipient/${recipientId}/xm-records`
        };

        return await dispatch(AuthActionCreators.apiGet(options));
    },

    getPostTransplant: (centerId: number, recipientId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IRecipientPostTransplantResponse> => {
        let options: IGet = {
            url: `${__API__}/center/${centerId}/recipient/${recipientId}/post-transplant`
        };

        return await dispatch(AuthActionCreators.apiGet(options));
    },
    updatePostTransplant: (centerId: number, recipientId: number, data: IRecipientProfilePostTransplantDataRequest) => async (dispatch: Dispatch<IApplicationState>): Promise<IRecipientPostTransplantResponse> => {
        let options = {
            url: `${__API__}/center/${centerId}/recipient/${recipientId}/post-transplant`,
            data
        };

        return await dispatch(AuthActionCreators.apiPut(options));
    },
    orderSwabKit: (recipientId: number, data: IRecipientContactInformation) => async (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options: IPost = {
            url: `${__API__}/recipient/${recipientId}/order-cheek-swab`,
            data
        };

        return await dispatch(AuthActionCreators.apiPost(options));
    },

    getAntibodyScreenings: (centerId: number, recipientId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IAntibodyScreeningResponse> => {
        let options: IGet = {
            url: `${__API__}/center/${centerId}/recipient/${recipientId}/antibody-screening`
        };

        return await dispatch(AuthActionCreators.apiGet(options));
    },
    getAntibodyScreeningResult: (centerId: number, recipientId: number, screeningId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IAntibodyScreeningResultResponse> => {
        let options: IGet = {
            url: `${__API__}/center/${centerId}/recipient/${recipientId}/antibody-screening-result/${screeningId}`
        };

        return await dispatch(AuthActionCreators.apiGet(options));
    },
    getAntibodyScreeningPdfFile: (centerId: number, recipientId: number, orderId: number) => (dispatch: Dispatch<IApplicationState>) => {
        let options: IGet = {
            url: __API__ + `/center/${centerId}/recipient/${recipientId}/antibody-screening-pdffile/${orderId}`,
            responseType: 'blob'
        };

        return dispatch(AuthActionCreators.apiGet(options));
    },
    getAntibodyScreeningRawFile: (centerId: number, recipientId: number, orderId: number) => (dispatch: Dispatch<IApplicationState>) => {
        let options: IGet = {
            url: __API__ + `/center/${centerId}/recipient/${recipientId}/antibody-screening-rawfile/${orderId}`,
            responseType: 'blob'
        };

        return dispatch(AuthActionCreators.apiGet(options));
    },
    processAntibodyScreeningOverride: (centerId: number, recipientId: number, screeningResultId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options: IPost = {
            url: `${__API__}/center/${centerId}/recipient/${recipientId}/antibody-screening-override/${screeningResultId}`,
        };

        return await dispatch(AuthActionCreators.apiPost(options));
    },
    getPairedDonors: (centerId: number, recipientId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IRecipientPairedDonorsListResponse> => {
        let options: IGet = {
            url: `${__API__}/center/${centerId}/recipient/${recipientId}/paired-donors`
        };

        return await dispatch(AuthActionCreators.apiGet(options));
    },
    deleteRecipient: (recipientId: number, reasonCode: number, reason: string, initials: string) => async (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options: IPost = {
            url: `${__API__}/recipient/${recipientId}/delete`,
            data: {
                reasonCode,
                reason,
                initials
            }
        };

        return await dispatch(AuthActionCreators.apiPost(options));
    },
    printDetails: (centerId: number, recipientId: number) => (dispatch: Dispatch<IApplicationState>): Promise<any> => {
        let options: IGet = {
            url: __API__ + `/center/${centerId}/recipient/${recipientId}/print-details`,
            responseType: 'blob'
        };

        return dispatch(AuthActionCreators.apiGet(options));
    },
    emailDetails: (centerId: number, recipientId: number, contactId: number) => (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        let options: IGet = {
            url: __API__ + `/center/${centerId}/recipient/${recipientId}/email-details?contactId=${contactId}`
        };

        return dispatch(AuthActionCreators.apiGet(options));
    }
};